import React from 'react';
import { Plus, PackageOpen, Power, PowerOff, Lock } from 'lucide-react';
import styles from './PackagesList.module.css';

// Context Import for permissions
import { useAuth } from '../../../../../../../context/AuthContext';

export default function PackagesList({ packagesList, onAddNew, onEdit, onToggleStatus, isLoading }) {
    const { user, hasPermission } = useAuth();

    // 🚀 PRECISE ACTION CHECKS (Blueprint Based)
    const isSuperAdmin = ['SUPERADMIN', 'ADMIN'].includes(user?.role);
    const canCreate = isSuperAdmin || hasPermission('packages', 'create');
    const canEdit = isSuperAdmin || hasPermission('packages', 'edit');
    const canDeactivate = isSuperAdmin || hasPermission('packages', 'deactivate');

    return (
        <div className={styles.listContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <h2>Service Packages</h2>
                    <p>Bundle services together, apply discounts, and sell upfront.</p>
                </div>
                
                {/* 🔒 CREATE PERMISSION CHECK */}
                {canCreate ? (
                    <button className={styles.primaryBtn} onClick={onAddNew}>
                        <Plus size={14} /> Create Package
                    </button>
                ) : (
                    <button className={styles.primaryBtnDisabled} disabled title="No permission to create packages">
                        <Lock size={12} /> Create Package
                    </button>
                )}
            </div>

            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <colgroup>
                        <col width="30%" />
                        <col width="15%" />
                        <col width="15%" />
                        <col width="15%" />
                        <col width="12%" />
                        <col width="13%" />
                    </colgroup>
                    <thead>
                        <tr>
                            <th>Package Name</th>
                            <th>Status</th>
                            <th className={styles.center}>Items Included</th>
                            <th className={styles.right}>Total Worth</th>
                            <th className={styles.right}>Selling Price</th>
                            <th className={styles.right}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {isLoading ? (
                            <tr>
                                <td colSpan="6">
                                    <div className={styles.emptyState}>
                                        <div className={styles.spinner}></div>
                                        <span>Loading packages...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : packagesList.length === 0 ? (
                            <tr>
                                <td colSpan="6">
                                    <div className={styles.emptyState}>
                                        <PackageOpen size={36} className={styles.emptyIcon} />
                                        <h4>No Packages Found</h4>
                                        <p>No service bundles have been created yet.</p>
                                        {canCreate && (
                                            <button className={styles.emptyStateBtn} onClick={onAddNew}>
                                                <Plus size={14}/> Create First Package
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            packagesList.map((pkg) => {
                                const isActive = pkg.status === 'ACTIVE';
                                return (
                                    <tr key={pkg.id} className={!isActive ? styles.rowInactive : ''}>
                                        <td>
                                            <div className={styles.pkgNameCell}>
                                                <span className={styles.pkgName} style={{color: isActive ? '#0f172a' : '#64748b'}}>
                                                    {pkg.name}
                                                </span>
                                                <span className={styles.pkgValidity}>{pkg.validity} Validity</span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className={isActive ? styles.statusActive : styles.statusArchived}>
                                                <span className={styles.statusDot}></span>
                                                {isActive ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className={styles.center}>
                                            <span className={styles.itemCountBadge} style={{opacity: isActive ? 1 : 0.6}}>
                                                {pkg.itemsCount} Items
                                            </span>
                                        </td>
                                        <td className={styles.right}>
                                            <span className={styles.worthPrice}>₹{pkg.totalWorth}</span>
                                        </td>
                                        <td className={styles.right}>
                                            <span className={styles.sellingPrice} style={{color: isActive ? 'var(--h-color-primary-700)' : '#64748b'}}>
                                                ₹{pkg.sellingPrice}
                                            </span>
                                        </td>
                                        <td className={styles.right}>
                                            <div className={styles.actionGroup}>
                                                
                                                {/* 🔒 EDIT PERMISSION CHECK */}
                                                {canEdit ? (
                                                    <button className={styles.actionBtn} onClick={() => onEdit(pkg)}>Edit</button>
                                                ) : (
                                                    <button className={styles.actionBtnDisabled} disabled title="Locked">
                                                        <Lock size={10}/> Edit
                                                    </button>
                                                )}

                                                {/* 🔒 DEACTIVATE PERMISSION CHECK */}
                                                {canDeactivate ? (
                                                    <button 
                                                        className={isActive ? styles.actionBtnDanger : styles.actionBtnSuccess} 
                                                        onClick={() => {
                                                            if(window.confirm(`Confirm to ${isActive ? 'deactivate' : 'activate'} this package?`)) {
                                                                onToggleStatus(pkg.id);
                                                            }
                                                        }}
                                                        title={isActive ? "Deactivate Package" : "Activate Package"}
                                                    >
                                                        {isActive ? <PowerOff size={12}/> : <Power size={12}/>}
                                                    </button>
                                                ) : (
                                                    <button className={styles.actionBtnDisabledIcon} disabled title="Toggle Locked">
                                                        <Lock size={12}/>
                                                    </button>
                                                )}

                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}